import {
  type MonthlyAccount,
  type PermitRateCouponOutput,
  type Reservation,
  type PermitCalculateRequest,
  type PermitCalculateResponse,
} from '@parkingbase/ecommerce-api-dev';
import { create } from 'zustand';
import { EcommerceSearchService } from '@/services/ecommerce-search.service';
import { ReservationService } from '@/services/reservation.service';
import { PermitRateService } from '@/services/permit-rate.service';
import { useAuthStore } from '@/stores/user.store';

import { logger } from '@/lib/logger';
import { getApiErrorMessage } from '@/lib/api/client-api';
import { normalizeCouponErrorMessage } from '@/lib/coupon-error-utils';
import {
  getApiErrorStatus,
  getGarageNotOpenDetails,
  isGarageNotOpenRefusal,
} from '@/lib/api-error-utils';
import { executeRecaptcha } from '@/lib/recaptcha';
import type {
  CouponOutcome,
  GarageNotOpenLookup,
  GarageSearchRequest,
  GarageResult,
  ZoneResult,
  CouponValidateRequest,
  CouponValidateResponse,
  CouponEventDetails,
  CouponRateOutput,
  EventOccurrenceResult,
} from '@/types/reserve.types';
import type { MonthlyRateSelection } from '@/types/monthly-rate-selection.types';
import type { PriceCycleKey } from '@/lib/permit-confirmation';
import type { SeriesOccurrenceRow } from '@/components/reserve/recurring-series-panel';

/**
 * PB-5177: the occurrence rows a recurring-event booking covers, snapshotted at pay time so the
 * downstream summary/confirmation surfaces can list them (a series pass covers all its dates; an
 * independent booking covers the picked ones). `isSeries` drives the read-only heading.
 */
export type ReservationOccurrenceSummary = {
  rows: SeriesOccurrenceRow[];
  isSeries: boolean;
};

const searchService = new EcommerceSearchService();
const reservationService = new ReservationService();
const permitRateService = new PermitRateService();

interface SearchParams {
  lat: number;
  lng: number;
  startDateTime: string;
  endDateTime: string;
  vehicleType: string;
  permitStartDate: string | null;
}

export interface EventDateHold {
  searchEnter: string;
  searchExit: string;
  eventEnter: string;
  eventExit: string;
}

export interface PendingDriver {
  id: string;
  fullName: string;
  mobile: string;
  driversLicenseNumber: string;
}

interface ReserveState {
  garageResults: GarageResult[];
  zoneResults: ZoneResult[];
  isSearching: boolean;
  /**
   * True when the last garage search threw rather than returning an empty list.
   *
   * ## Invariants:
   * - Must stay distinct from `garageResults.length === 0`. PB-4648 observed that a failed
   *   search and a genuinely empty one render identically and made the LOG line the only way to
   *   tell them apart; this flag is the on-screen half of that, so a product layout can show an
   *   error state with a retry instead of "no products available" (PB-5345).
   * - Cleared at the start of every search, so a recovered search never renders stale failure.
   */
  searchFailed: boolean;
  /**
   * The last request handed to `searchGarages`, retained so a failed search can be retried.
   *
   * ## Invariants:
   * - Distinct from `searchParams`, which is a derived display shape and cannot be replayed.
   *   The error state's Try-again action needs the actual request or it has nothing to re-issue.
   */
  lastSearchRequest: GarageSearchRequest | null;
  /**
   * The coupon code currently APPLIED to the result set — not what the customer is typing.
   *
   * ## Invariants:
   * - Null until a coupon search SUCCEEDS. A code that failed in transport is not applied, so
   *   the chip must not appear for it; the input keeps the text and the error sits beside it.
   * - Cleared by an ordinary search. A new location or date range produces a different result
   *   set, and carrying a stale "2 products unlocked" chip onto it would attribute products to
   *   a code that was never re-submitted.
   */
  appliedCouponCode: string | null;
  /**
   * The server's verdict on the applied code.
   *
   * ## Invariants:
   * - NULL means the coupon path never ran, and it is a third thing distinct from both enum
   *   values — matching the wire, where the field is absent rather than `NOTHING_REVEALED`.
   *   Rendering "your code did nothing" to a customer who entered nothing is what that
   *   distinction prevents.
   * - `LOOKUP_UNAVAILABLE` is NOT a reveal verdict. It reports that the read failed, so it must
   *   render as its own state rather than collapsing into either of the other two.
   */
  couponOutcome: CouponOutcome | null;
  /**
   * How many products the applied code unlocked.
   *
   * ## Invariants:
   * - Can be NON-ZERO while `couponOutcome` is `LOOKUP_UNAVAILABLE`: one company's read failed
   *   while another succeeded. The count stays valid and the set is presented as incomplete —
   *   do not zero it to match the outcome.
   */
  couponRevealCount: number | null;
  /**
   * A coupon request that failed in TRANSPORT — network, server error, rejected captcha, or the
   * throttle.
   *
   * ## Invariants:
   * - Never set from a coupon OUTCOME. `NOTHING_REVEALED` is a successful response and must
   *   never reach this flag; collapsing the two makes a working code read as broken, which is
   *   the single defect this feature is most likely to ship.
   * - Distinct from `searchFailed`, which owns the whole-page error state. A failed coupon
   *   apply leaves the existing products on screen (the list is unchanged), so it cannot reuse
   *   the flag that blanks them.
   */
  couponFailed: boolean;
  isApplyingCoupon: boolean;
  /**
   * Server-resolved multiple-permit-booking gate from the garage search
   * (legacy `enableMonthlyTab` / `theme.disableMultiplePermitBooking` parity).
   * When false, every monthly booking entry point (mode toggle, monthly tab,
   * `?mode=monthly` deep links) must be hidden/clamped to hourly — except for
   * garages offering an exempt rate (see lib/multiple-permit-gate.ts).
   * Defaults true (fail-open) until a search response says otherwise —
   * matching legacy, which also gates only after account data loads.
   */
  canBookNewPermit: boolean;

  selectedGarageId: string | null;
  selectedRate: MonthlyRateSelection | null;
  selectedVehicleId: string | null;

  couponCode: string | null;
  /**
   * rateId from a special-rate deep link (`?rateId=specialRate::…`), kept so a
   * date change can re-run the lookup with the original link parameter — the
   * response's specialRateId can be a different family member and must not be
   * echoed back as the request rateId.
   */
  couponRateId: string | null;
  couponResponse: CouponValidateResponse | null;
  isCouponValidating: boolean;
  couponError: string | null;
  /**
   * PB-1981 — true when the looked-up event has requireCouponForAccess. This
   * is a booking gate, not an error: the API returns 200 with
   * couponRequired=true and no rate outputs when the code is missing. The
   * flag is sticky for the currently selected event — a failed apply or a
   * removed coupon must NOT clear it (that would let one typo bypass the
   * gate). It resets only when the event selection changes (selectEvent) or
   * the search is cleared; the UI treats the gate as satisfied while a valid
   * coupon is applied (couponResponse.valid).
   */
  couponRequired: boolean;
  couponRequiredMessage: string | null;

  searchParams: SearchParams | null;

  /** Drivers added locally during the monthly signup flow (not yet saved to API) */
  pendingDrivers: PendingDriver[];
  addPendingDriver: (driver: Omit<PendingDriver, 'id'>) => void;
  updatePendingDriver: (id: string, driver: Omit<PendingDriver, 'id'>) => void;
  removePendingDriver: (id: string) => void;
  /** Clears all booking/search state incl. pendingDrivers (PII) — call on logout. */
  reset: () => void;

  /** Monthly coupon state */
  monthlyCouponCode: string | null;
  monthlyCouponResponse: PermitRateCouponOutput | null;
  isMonthlyCouponApplying: boolean;
  monthlyCouponError: string | null;
  /**
   * PB-3543: specialRateId from a monthly rateId deep link. useGarageDetail
   * re-derives the monthly override from it on every date-change re-map (the
   * link param itself only lives in useDirectGarage). Cleared on a fresh search.
   *
   * PB-5425: set from the link itself, not from the server's verdict — legacy
   * keys its bypass on the URL param alone (GarageListPage.tsx:5390) and never
   * revokes it when applyDiscount throws.
   */
  monthlyLinkRateId: string | null;

  /** Permit calculation result from /v2/permits/calculate */
  permitCalculation: PermitCalculateResponse | null;
  isCalculatingPermit: boolean;
  permitCalculationError: string | null;

  /** Event occurrences for the selected garage */
  eventOccurrences: EventOccurrenceResult[];
  isLoadingEvents: boolean;
  selectedEventId: string | null;
  eventDateHold: EventDateHold | null;
  setEventDateHold: (hold: EventDateHold | null) => void;

  /**
   * PB-2947: true while a debounced series/independent price re-quote is in
   * flight (or its debounce is pending) so the storefront can show a skeleton on
   * the price and disable the Pay button. Set by the pick-requote effect; the
   * silent requote itself leaves the response and other flags untouched.
   */
  isCalculatingSeries: boolean;
  setCalculatingSeries: (calculating: boolean) => void;

  /**
   * PB-2947 (independent booking): the customer's picked occurrence start
   * times (ISO strings) on a pick-your-dates recurring event. Empty for
   * series and non-recurring events.
   */
  selectedOccurrenceStarts: string[];
  toggleOccurrenceSelection: (occurrenceStart: string) => void;
  setOccurrenceSelection: (occurrenceStarts: string[]) => void;
  clearOccurrenceSelection: () => void;

  searchGarages: (request: GarageSearchRequest) => Promise<void>;
  /**
   * Asks the api whether a direct-link identifier names a location that is still onboarding
   * (PB-7765). Resolves `{ outcome: 'not-open' }` only for a 404 `GARAGE_NOT_OPEN`, carrying the
   * location's `garageName` / `garageAddress` when the body has them (PB-8745); everything else,
   * including a failed request, is `{ outcome: 'other' }`. Touches no store state.
   */
  lookupGarageNotOpen: (identifier: string) => Promise<GarageNotOpenLookup>;
  /** Re-runs the last search carrying a coupon code. Leaves the current products alone on failure. */
  applyCoupon: (code: string) => Promise<void>;
  /** Drops the applied code and re-runs the last search without it. */
  clearCoupon: () => Promise<void>;
  /** Re-search with updated times; updates results without clearing the list first */
  refreshRates: (request: GarageSearchRequest) => Promise<void>;
  validateCoupon: (request: CouponValidateRequest) => Promise<void>;
  removeCoupon: () => void;
  /** Clear only the coupon error (user edited the code) — keeps any applied coupon. */
  clearCouponError: () => void;
  /** Clear only the monthly coupon error (user edited the code) — keeps any applied coupon. */
  clearMonthlyCouponError: () => void;
  applyMonthlyCoupon: (
    company: string,
    garageId: string,
    couponCode: string,
    startDate: string
  ) => Promise<PermitRateCouponOutput | null>;
  removeMonthlyCoupon: () => void;
  /**
   * PB-3543: resolves a monthly rateId deep link through
   * `/permitRateCard/applyDiscount` (the api enforces the rate's "Use Rate In
   * Link" toggle).
   *
   * ## Invariants:
   * - Grants `monthlyLinkRateId` from the link itself, never from the verdict,
   *   and does not revoke it on rejection. Why: legacy keys its bypass on the
   *   URL param alone and treats a throw as a toast, not a loss of access
   *   (GarageListPage.tsx:3815-3854, `applyMonthlyCouponClick`).
   * - Writes `monthlyCouponResponse` but never `monthlyCouponCode`. Why: a
   *   link-only rate card reaches the client ONLY in this response — the search
   *   listing emits BOARD_RATE cards alone (GarageServiceImpl.java:1070), and a
   *   SPECIAL_RATE joins it solely as a VIP/loyalty discount rate (:1039) — yet
   *   no code was typed, so the coupon input must stay empty. Legacy does the
   *   same: it sets the response and leaves its `monthlyCoupon` state ''.
   */
  validateMonthlyLinkRate: (
    company: string,
    garageId: string,
    specialRateId: string,
    couponCode: string,
    startDate: string
  ) => Promise<{ granted: boolean; errorMessage?: string }>;
  selectGarage: (garageId: string) => void;
  selectRate: (rate: MonthlyRateSelection | null) => void;
  setSelectedVehicleId: (vehicleId: string | null) => void;
  /** Update search date/time params without re-fetching rates */
  updateSearchDates: (startDateTime: string, endDateTime: string) => void;
  /** Sync the chosen vehicle type into searchParams so the payment page quotes it. (PB-2600) */
  updateSearchVehicleType: (vehicleType: string) => void;
  /** Call /v2/permits/calculate before navigating to monthly payment */
  calculatePermit: (request: PermitCalculateRequest) => Promise<PermitCalculateResponse | null>;
  fetchEventsForGarage: (
    garageId: string,
    startDateTime: string,
    endDateTime: string
  ) => Promise<void>;
  selectEvent: (eventId: string | null) => void;
  clearSearch: () => void;
  /**
   * Epoch ms at which the hourly checkout hold expires, or null when no checkout
   * is in flight. Lives here rather than in the payment screen's own state so
   * that leaving payment and coming back (e.g. via the header menu) resumes the
   * same countdown instead of granting a fresh five minutes.
   */
  paymentDeadlineAt: number | null;
  setPaymentDeadline: (deadlineAt: number | null) => void;
  createdReservation: Reservation | null;
  setCreatedReservation: (reservation: Reservation | null) => void;
  /** Booked garage, kept past clearSearch so the confirmation screen can still render it. */
  createdReservationGarage: GarageResult | null;
  setCreatedReservationGarage: (garage: GarageResult | null) => void;
  /**
   * PB-5177: occurrence rows a recurring-event booking covers (series = all dates, independent =
   * picked dates), snapshotted at pay time and — like createdReservation — kept past clearSearch
   * so BOTH the payment confirmation view and the /reserve/confirmation detail page can list every
   * booked date. Null for a normal single-session booking.
   */
  createdReservationOccurrenceSummary: ReservationOccurrenceSummary | null;
  setCreatedReservationOccurrenceSummary: (summary: ReservationOccurrenceSummary | null) => void;
  /**
   * PB-5604: the monthly account a signup just created, snapshotted at submit time so the
   * signup page can render its own confirmation instead of redirecting silently to the
   * account list. Kept past `clearSearch` for the same reason `createdReservation` is — the
   * confirmation renders after the booking context has been torn down.
   */
  createdMonthlyAccount: MonthlyAccount | null;
  setCreatedMonthlyAccount: (account: MonthlyAccount | null) => void;
  /** Garage the monthly signup was for, kept past clearSearch alongside the account. */
  createdMonthlyAccountGarage: GarageResult | null;
  setCreatedMonthlyAccountGarage: (garage: GarageResult | null) => void;
  /**
   * Formatted rate for the permit just bought, e.g. "$185.00". Snapshotted for the same reason
   * as the account: `clearSearch` nulls `selectedRate`, so the rate the confirmation needs is
   * gone by the time it renders and the price rendered blank (PB-5604).
   */
  createdMonthlyAccountPrice: string | null;
  setCreatedMonthlyAccountPrice: (price: string | null) => void;
  /** Cadence label key for that price ("per week"), snapshotted with it for the same reason. */
  createdMonthlyAccountPriceCycle: PriceCycleKey | null;
  setCreatedMonthlyAccountPriceCycle: (cycle: PriceCycleKey | null) => void;

  /** Guest pass count selected during monthly booking */
  guestPassCount: number;
  setGuestPassCount: (count: number) => void;
  /** Selected optional fee IDs during monthly booking */
  selectedFeeIds: string[];
  setSelectedFeeIds: (ids: string[]) => void;
}

let latestPermitCalcRequest = 0;

export const useReserveStore = create<ReserveState>((set, get) => ({
  garageResults: [],
  zoneResults: [],
  isSearching: false,
  searchFailed: false,
  lastSearchRequest: null,
  canBookNewPermit: true,
  appliedCouponCode: null,
  couponOutcome: null,
  couponRevealCount: null,
  couponFailed: false,
  isApplyingCoupon: false,

  selectedGarageId: null,
  selectedRate: null,
  selectedVehicleId: null,

  couponCode: null,
  couponRateId: null,
  couponResponse: null,
  isCouponValidating: false,
  couponError: null,
  couponRequired: false,
  couponRequiredMessage: null,

  monthlyCouponCode: null,
  monthlyCouponResponse: null,
  isMonthlyCouponApplying: false,
  monthlyCouponError: null,
  monthlyLinkRateId: null,

  permitCalculation: null,
  isCalculatingPermit: false,
  permitCalculationError: null,

  eventOccurrences: [],
  isLoadingEvents: false,
  selectedEventId: null,
  eventDateHold: null,
  setEventDateHold: (hold) => set({ eventDateHold: hold }),
  isCalculatingSeries: false,

  selectedOccurrenceStarts: [],
  toggleOccurrenceSelection: (occurrenceStart: string) =>
    set((state) => ({
      selectedOccurrenceStarts: state.selectedOccurrenceStarts.includes(occurrenceStart)
        ? state.selectedOccurrenceStarts.filter((s) => s !== occurrenceStart)
        : [...state.selectedOccurrenceStarts, occurrenceStart],
    })),
  setOccurrenceSelection: (occurrenceStarts: string[]) =>
    set({ selectedOccurrenceStarts: occurrenceStarts }),
  clearOccurrenceSelection: () => set({ selectedOccurrenceStarts: [] }),
  setCalculatingSeries: (calculating: boolean) => set({ isCalculatingSeries: calculating }),

  searchParams: null,

  pendingDrivers: [],
  addPendingDriver: (driver) =>
    set((state) => ({
      pendingDrivers: [
        ...state.pendingDrivers,
        { ...driver, id: `driver-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` },
      ],
    })),
  updatePendingDriver: (id, driver) =>
    set((state) => ({
      pendingDrivers: state.pendingDrivers.map((d) => (d.id === id ? { ...d, ...driver } : d)),
    })),
  removePendingDriver: (id) =>
    set((state) => ({
      pendingDrivers: state.pendingDrivers.filter((d) => d.id !== id),
    })),

  paymentDeadlineAt: null,
  setPaymentDeadline: (deadlineAt) => set({ paymentDeadlineAt: deadlineAt }),

  createdReservation: null,
  setCreatedReservation: (reservation) => set({ createdReservation: reservation }),

  createdReservationGarage: null,
  setCreatedReservationGarage: (garage) => set({ createdReservationGarage: garage }),

  createdReservationOccurrenceSummary: null,
  setCreatedReservationOccurrenceSummary: (summary) =>
    set({ createdReservationOccurrenceSummary: summary }),

  createdMonthlyAccount: null,
  setCreatedMonthlyAccount: (account) => set({ createdMonthlyAccount: account }),

  createdMonthlyAccountGarage: null,
  setCreatedMonthlyAccountGarage: (garage) => set({ createdMonthlyAccountGarage: garage }),

  createdMonthlyAccountPrice: null,
  setCreatedMonthlyAccountPrice: (price) => set({ createdMonthlyAccountPrice: price }),

  createdMonthlyAccountPriceCycle: null,
  setCreatedMonthlyAccountPriceCycle: (cycle) => set({ createdMonthlyAccountPriceCycle: cycle }),

  guestPassCount: 0,
  setGuestPassCount: (count) => set({ guestPassCount: count }),
  selectedFeeIds: [],
  setSelectedFeeIds: (ids) => set({ selectedFeeIds: ids }),

  reset: () =>
    set({
      garageResults: [],
      zoneResults: [],
      isSearching: false,
      selectedGarageId: null,
      selectedRate: null,
      selectedVehicleId: null,
      couponCode: null,
      couponResponse: null,
      isCouponValidating: false,
      couponError: null,
      couponRequired: false,
      couponRequiredMessage: null,
      monthlyCouponCode: null,
      monthlyCouponResponse: null,
      isMonthlyCouponApplying: false,
      monthlyCouponError: null,
      permitCalculation: null,
      isCalculatingPermit: false,
      permitCalculationError: null,
      eventOccurrences: [],
      isLoadingEvents: false,
      selectedEventId: null,
      eventDateHold: null,
      searchParams: null,
      pendingDrivers: [],
      createdReservation: null,
      createdReservationGarage: null,
      createdReservationOccurrenceSummary: null,
      createdMonthlyAccount: null,
      createdMonthlyAccountGarage: null,
      createdMonthlyAccountPrice: null,
      createdMonthlyAccountPriceCycle: null,
      guestPassCount: 0,
      selectedFeeIds: [],
      paymentDeadlineAt: null,
    }),

  /**
   * Applies a coupon code to the CURRENT result set.
   *
   * ## Invariants:
   * - **The products on screen are left alone until the request succeeds.** `searchGarages`
   *   blanks `garageResults` the moment it starts and again if it throws, which is right for a
   *   new search and wrong here: AC 3 requires the list to be unchanged when a coupon request
   *   fails. So this issues the request through the service directly and commits results only
   *   on success, rather than re-entering that action.
   * - A TRANSPORT failure sets `couponFailed` and never `searchFailed`. The first shows an error
   *   beside the input; the second blanks the page. A coupon that could not be checked is not a
   *   reason to take the customer's results away.
   * - A coupon OUTCOME never reaches `couponFailed`, including `NOTHING_REVEALED` and
   *   `LOOKUP_UNAVAILABLE`. Both are successful responses that the UI renders as their own
   *   states; treating either as an error is what makes a working code read as broken.
   * - No-ops when there is no `lastSearchRequest` — there is no result set to widen yet, and
   *   fabricating a search from scratch here would ignore the customer's location and dates.
   */
  applyCoupon: async (code: string) => {
    const base = get().lastSearchRequest;
    const trimmed = code.trim();
    if (!base || trimmed === '') return;

    set({ isApplyingCoupon: true, couponFailed: false });

    try {
      const response = await searchService.searchGarages({ ...base, couponCode: trimmed });
      set({
        garageResults: response.garages ?? [],
        zoneResults: response.zones ?? [],
        canBookNewPermit: response.canBookNewPermit ?? true,
        isApplyingCoupon: false,
        // Applied iff the coupon path actually ran. On the `LOOKUP_UNAVAILABLE` branch it did
        // run — the read failed, which is a result — so the chip appears and reports that state
        // rather than the input silently keeping the code with nothing shown.
        appliedCouponCode: response.couponOutcome != null ? trimmed : null,
        couponOutcome: response.couponOutcome ?? null,
        couponRevealCount: response.couponRevealCount ?? null,
      });
    } catch (error) {
      logger.error('Failed to apply coupon to search', error, {
        feature: 'reserve-store',
        route: 'applyCoupon',
        status: getApiErrorStatus(error),
      });
      // Deliberately NOT clearing garageResults, and deliberately not touching searchFailed.
      set({
        isApplyingCoupon: false,
        couponFailed: true,
        appliedCouponCode: null,
        couponOutcome: null,
        couponRevealCount: null,
      });
    }
  },

  /**
   * Removes the applied code and returns the list to what it was without it.
   *
   * ## Invariants:
   * - Re-runs the search rather than filtering the revealed products out client-side. The
   *   revealed cards are interleaved in their normal sort position, so removing them locally
   *   would need the pre-coupon ordering reconstructed — and any drift between that and what
   *   the server would return shows up as products shuffling for no visible reason.
   * - Clears the coupon state even if the re-search fails, because the code is no longer
   *   applied either way; `searchGarages` owns whatever error state that produces.
   */
  clearCoupon: async () => {
    const base = get().lastSearchRequest;
    set({
      appliedCouponCode: null,
      couponOutcome: null,
      couponRevealCount: null,
      couponFailed: false,
    });
    if (!base) return;
    // The key must be ABSENT, not undefined. A destructure-and-discard would say this most
    // directly but leaves an unused binding, which is an error under this repo config;
    // `couponCode: undefined` leaves the key PRESENT, which the replay contract forbids
    // (asserted by `not.toHaveProperty`, and caught there when this was written that way).
    const withoutCoupon = { ...base };
    delete withoutCoupon.couponCode;
    await get().searchGarages(withoutCoupon);
  },

  searchGarages: async (request: GarageSearchRequest) => {
    set({
      isSearching: true,
      searchFailed: false,
      lastSearchRequest: request,
      garageResults: [],
      zoneResults: [],
      // An ordinary search is a NEW result set, so any applied code stops describing it.
      //
      // `applyCoupon` deliberately does NOT route through this action — see its docblock — and
      // this clear is safe only BECAUSE of that. An earlier version of this comment claimed the
      // opposite, which is worse than saying nothing: the lines below blank `garageResults` on
      // entry and the catch blanks them again, so a reader who believed the coupon path came
      // through here would think AC 3's "the product list is unchanged" had already survived
      // that route. It has not; it survives by avoiding it.
      appliedCouponCode: null,
      couponOutcome: null,
      couponRevealCount: null,
      couponFailed: false,
      searchParams: {
        lat: request.latitude,
        lng: request.longitude,
        startDateTime: request.startDateTime,
        endDateTime: request.endDateTime,
        vehicleType: request.vehicleInfo?.oversizeType ?? 'STANDARD',
        permitStartDate: request.permitStartDate ?? null,
      },
    });

    try {
      const response = await searchService.searchGarages(request);
      set({
        garageResults: response.garages ?? [],
        zoneResults: response.zones ?? [],
        canBookNewPermit: response.canBookNewPermit ?? true,
        isSearching: false,
        // `?? null` and NOT `?? 0` / `?? 'NOTHING_REVEALED'`: absent means the coupon path never
        // ran, which is a different answer from "ran and found nothing". Defaulting either field
        // would make every ordinary search look like a coupon that revealed nothing.
        appliedCouponCode: response.couponOutcome != null ? (request.couponCode ?? null) : null,
        couponOutcome: response.couponOutcome ?? null,
        couponRevealCount: response.couponRevealCount ?? null,
      });
    } catch (error) {
      // Status is carried explicitly: an empty garage list is indistinguishable
      // from a failed search on the screen, so this log line is the only place a
      // recurrence can be told apart from a genuinely empty result (PB-4648).
      logger.error('Failed to search garages', error, {
        feature: 'reserve-store',
        route: 'searchGarages',
        status: getApiErrorStatus(error),
      });
      // PB-5345: also record the failure in state. The log above is not reachable by the UI,
      // so without this the product layouts would render "no products available" for an
      // outage — the empty state that deliberately offers no retry.
      set({ garageResults: [], zoneResults: [], isSearching: false, searchFailed: true });
    }
  },

  lookupGarageNotOpen: async (identifier: string): Promise<GarageNotOpenLookup> => {
    const base = get().lastSearchRequest;
    if (!base) return { outcome: 'other' };
    try {
      // Reuses the list request's latitude / longitude / start / end because the api requires
      // all four; garageId is dropped so the api resolves `locationCode` instead. Deliberately
      // NOT routed through `searchGarages`, which would blank the result list on entry.
      await searchService.searchGarages({
        ...base,
        garageId: undefined,
        locationCode: identifier,
      });
      return { outcome: 'other' };
    } catch (error) {
      if (isGarageNotOpenRefusal(error)) {
        return { outcome: 'not-open', ...getGarageNotOpenDetails(error) };
      }
      logger.error('Failed to look up garage by location code', error, {
        feature: 'reserve-store',
        route: 'lookupGarageNotOpen',
        status: getApiErrorStatus(error),
      });
      return { outcome: 'other' };
    }
  },

  /**
   * Re-quotes some garages in place — the detail drawer fires this whenever the customer
   * changes dates.
   *
   * ## Invariants:
   * - **Carries any APPLIED coupon code into the refresh (PB-5582).** Without it the refreshed
   *   garages come back with no `couponRevealedPermitRates`, so the unlocked rows vanish from
   *   the list while the chip still says "2 more products unlocked" — the headline contradicting
   *   the page, with nothing to notice it by. Re-sending the code is better than clearing the
   *   coupon state: it keeps the products the customer unlocked rather than taking them away
   *   because they nudged an exit time.
   * - **Does NOT overwrite `couponOutcome` or `couponRevealCount` from the response.** A refresh
   *   is scoped to a SUBSET of garages, so the count it returns describes that subset, not the
   *   whole result set. Writing it would silently shrink "2 more products unlocked" to whatever
   *   the one refreshed garage happened to unlock.
   * - **Carries the refresh's DATES into `lastSearchRequest` — and nothing else.** That field is
   *   what `clearCoupon`, `applyCoupon` and `retrySearch` all replay, so leaving it stale meant
   *   Remove re-searched with the PRE-refresh dates and silently reverted the change the
   *   customer had just made.
   *
   *   **It must NOT adopt the refresh's SCOPE.** The only caller from the results screen is the
   *   drawer's date effect, which always passes `garageId` — a single-garage request. An earlier
   *   version of this stored that request wholesale, so after any date nudge in an open drawer
   *   the replayed search was scoped to one garage and Remove collapsed a multi-garage result
   *   list down to it. That is AC 5 failing outright ("the list returns to its prior state"),
   *   reachable from an ordinary flow, and the same poisoning applied to a coupon applied after
   *   a nudge. Merging only the fields a refresh legitimately changes is what keeps the replay
   *   describing the customer's actual search.
   */
  refreshRates: async (request: GarageSearchRequest) => {
    const applied = get().appliedCouponCode;
    const effectiveRequest = applied ? { ...request, couponCode: applied } : request;

    set({
      searchParams: {
        lat: request.latitude,
        lng: request.longitude,
        startDateTime: request.startDateTime,
        endDateTime: request.endDateTime,
        vehicleType: request.vehicleInfo?.oversizeType ?? 'STANDARD',
        permitStartDate: request.permitStartDate ?? null,
      },
      // Dates only — deliberately NOT `effectiveRequest`, which carries the drawer's
      // single-garage `garageId`. See the invariant above.
      lastSearchRequest: {
        ...(get().lastSearchRequest ?? request),
        startDateTime: request.startDateTime,
        endDateTime: request.endDateTime,
        permitStartDate: request.permitStartDate,
        vehicleInfo: request.vehicleInfo,
        // COUPON-FREE, deliberately. This field is the replayable customer SEARCH, and
        // `retrySearch` re-issues it verbatim for the whole-page Try-again. Stamping the code
        // in made that retry coupon-bearing, so it executed a captcha and took the throttled
        // path — and in an environment with no site key the service throws before the request
        // goes out, making Try-again a button that can never recover. Both `applyCoupon` and
        // `refreshRates` read `appliedCouponCode` from the store and compose the code onto the
        // request at the call site, so keeping it out of here costs nothing.
        couponCode: undefined,
      },
    });

    try {
      const response = await searchService.searchGarages(effectiveRequest);
      const prev = useReserveStore.getState();

      // Merge updated garages into existing results (preserve zones + other garages)
      const garages = response.garages ?? [];
      const updatedIds = new Set(garages.map((g) => g.garageId));
      const unchanged = prev.garageResults.filter((g) => !updatedIds.has(g.garageId));

      // Did this refresh actually TAKE AWAY revealed inventory? That is the only thing that
      // makes the applied chip wrong, and it is observable here rather than inferred.
      const lostRevealed = garages.some((g) => {
        const before = prev.garageResults.find((p) => p.garageId === g.garageId);
        return (
          (before?.couponRevealedPermitRates?.length ?? 0) > 0 &&
          (g.couponRevealedPermitRates?.length ?? 0) === 0
        );
      });

      set({
        garageResults: [...unchanged, ...garages],
        zoneResults: response.zones ?? prev.zoneResults,
        canBookNewPermit: response.canBookNewPermit ?? true,
        // Three-way, and the third arm is the point: this flag is only written when the
        // refresh actually says something about the code.
        //
        //   lost rows        -> stale. Observed, not inferred: whatever the outcome claims,
        //                       inventory the chip is counting has gone.
        //   outcome APPLIED  -> clear. The server's own positive assertion that the code
        //                       revealed something in THIS response.
        //   anything else    -> leave it alone. The refresh is uninformative.
        //
        // Two earlier versions each got one direction right and the other wrong, and the
        // third arm is what reconciles them. Keying on transport success cleared the flag on
        // a 2xx `LOOKUP_UNAVAILABLE`/`NOTHING_REVEALED` that had just dropped this garage's
        // revealed rows — the warning suppressed exactly when it was true. Keying on the raw
        // outcome instead flipped it the other way: `refreshRates` is SINGLE-GARAGE scoped
        // (the drawer's date effect always passes `garageId`) and the server computes the
        // outcome over the garages in that response alone, so nudging a date in the drawer of
        // any garage where the code reveals nothing returned `NOTHING_REVEALED`, dropped no
        // rows anywhere, and still raised the stale warning over a page that did reflect the
        // code. In an N-garage result set that is N-1 false warnings, one per drawer.
        //
        // Both are the same failure — a signal that does not track the thing it reports — so
        // this keys on the thing itself. Still only when `applied`: a coupon-free refresh
        // re-verifies nothing either way.
        ...(applied && lostRevealed ? { couponFailed: true } : {}),
        ...(applied && !lostRevealed && response.couponOutcome === 'APPLIED'
          ? { couponFailed: false }
          : {}),
      });
    } catch (error) {
      logger.error('Failed to refresh rates', error, {
        feature: 'reserve-store',
        route: 'refreshRates',
        couponBearing: applied != null,
      });

      // A coupon-bearing refresh has two new ways to fail that an ordinary one does not: the
      // mandatory captcha, and the coupon throttle — and a date-picker drag issues several.
      // `searchParams` was already moved to the new dates before the request, so swallowing
      // this leaves the panel showing a new window beside the old quote, with nothing saying
      // the refresh did not happen.
      //
      // Retry WITHOUT the code rather than surfacing an error: the customer changed a date,
      // not a coupon, so the useful outcome is fresh public prices with only the revealed rows
      // going stale. `couponFailed` then says the code is no longer reflected in what they see.
      if (applied) {
        try {
          const plain = await searchService.searchGarages(request);
          const prev = useReserveStore.getState();
          const garages = plain.garages ?? [];
          const updatedIds = new Set(garages.map((g) => g.garageId));
          set({
            garageResults: [
              ...prev.garageResults.filter((g) => !updatedIds.has(g.garageId)),
              ...garages,
            ],
            zoneResults: plain.zones ?? prev.zoneResults,
            couponFailed: true,
          });
        } catch (retryError) {
          logger.error('Coupon-free refresh retry also failed', retryError, {
            feature: 'reserve-store',
            route: 'refreshRates',
          });
          // BOTH attempts failed, so the rows on screen predate the dates already written to
          // `searchParams` — strictly more stale than the single-failure case just above, which
          // does set this. Leaving it false was the quiet half of the sticky bug: the chip kept
          // announcing a reveal count for inventory that no longer matches the search, and the
          // one state that says so was reachable only on the path that half-succeeded.
          set({ couponFailed: true });
        }
      }
    }
  },

  validateCoupon: async (request: CouponValidateRequest) => {
    // Deliberately does NOT reset couponRequired/couponRequiredMessage: the
    // gate must survive a rejected apply (the catch below) or Pay would
    // re-enable on a gated event after one bad code. The gate resets in
    // selectEvent/clearSearch instead.
    //
    // silent (PB-2947 pick requote): leave the current response and flags in
    // place — the customer just toggled a date checkbox, and the only thing
    // that should visibly change is the rate once the fresh quote lands.
    if (!request.silent) {
      set({
        isCouponValidating: true,
        couponError: null,
        couponResponse: null,
        couponCode: request.couponCode ?? null,
        couponRateId: request.rateId ?? null,
      });
    }

    try {
      const recaptchaToken = await executeRecaptcha('validate_coupon');
      const state = useReserveStore.getState();
      // PB-2947 (independent booking): picked occurrence starts ride the lookup
      // so the API quotes exactly those windows. Extra body property until
      // @parkingbase/ecommerce-api-dev republishes with the new field.
      const selectedOccurrences = request.selectedOccurrenceStarts?.length
        ? request.selectedOccurrenceStarts.map((start) => ({ start }))
        : undefined;
      const lookupResponse = await reservationService.lookupSpecialRate({
        garageId: request.garageId,
        companyId: request.company,
        code: request.couponCode,
        rateId: request.rateId,
        eventId: request.eventId,
        enter: request.startDateTime,
        exit: request.endDateTime,
        // Legacy sends both on every lookup — vehicle type gates rate
        // applicability, userId gates per-user coupon usage limits.
        vehicleType: request.vehicleType ?? state.searchParams?.vehicleType ?? 'STANDARD',
        userId: useAuthStore.getState().user?.userId ?? '',
        recaptchaToken,
        ...(selectedOccurrences ? { selectedOccurrences } : {}),
      });

      const eventDetails: CouponEventDetails | undefined = lookupResponse.eventDetails
        ? {
            eventId: lookupResponse.eventDetails.id ?? '',
            eventName: lookupResponse.eventDetails.specialRateName ?? '',
            discountPercent: 0,
            externalEventDescription: lookupResponse.eventDetails.externalEventDescription,
            dateRangeType: lookupResponse.eventDetails.dateRangeType as
              | CouponEventDetails['dateRangeType']
              | undefined,
            checkinAfterTime: lookupResponse.checkinAfterTime,
            checkoutBeforeTime: lookupResponse.checkoutBeforeTime,
            // PB-2947: recurring-series detection — drives the fixed-dates lock
            // and the occurrence list's schedule-name labels.
            recurring: lookupResponse.eventDetails.recurring,
            independentBooking: (
              lookupResponse.eventDetails as {
                // On the wire since the PB-2947 api change; typed once
                // @parkingbase/ecommerce-api-dev republishes with the new field.
                independentBooking?: boolean;
              }
            ).independentBooking,
            // "Override Event Dates" — gates the storefront date-range filter (PB-2947).
            // On the wire from EventDetails; typed via cast until api-dev republishes.
            customBookingDates: (
              lookupResponse.eventDetails as {
                customBookingDates?: boolean;
              }
            ).customBookingDates,
            // The event's full window (PB-2947) — seeds/bounds the Override Event Dates widget to
            // the whole event, not the narrow single-occurrence booking window.
            eventStartDateTime: (
              lookupResponse.eventDetails as {
                startDateTime?: string;
              }
            ).startDateTime,
            eventEndDateTime: (
              lookupResponse.eventDetails as {
                endDateTime?: string;
              }
            ).endDateTime,
            recurrenceSchedules: lookupResponse.eventDetails.recurrenceSchedules?.map(
              (schedule) => ({
                // `name` is on the wire since the PB-2947 api change; typed once
                // @parkingbase/ecommerce-api-dev republishes.
                name: (schedule as { name?: string }).name,
              })
            ),
            accessGroupIds: (lookupResponse.eventDetails.accessGroups ?? [])
              .map((group) => group.accessGroupId)
              .filter((id): id is string => !!id),
          }
        : undefined;

      if (lookupResponse.couponRequired) {
        // PB-1981: requireCouponForAccess gate — the event needs a coupon code
        // before booking. Not an error: eventDetails is still populated so the
        // event time-window hydration in useDirectGarage keeps working.
        set({
          couponResponse: {
            valid: false,
            message: lookupResponse.couponRequiredMessage,
            eventDetails,
          },
          couponRequired: true,
          couponRequiredMessage: lookupResponse.couponRequiredMessage ?? null,
          isCouponValidating: false,
        });
        return;
      }

      const rod = lookupResponse.rateOutputWithDiscount;
      const discountRate = rod?.rate ?? 0;
      const originalRate = lookupResponse.rateOutputWithoutDiscount?.rate ?? 0;
      const discountAmount = originalRate - discountRate;

      // PB-2947: a recurring SERIES is priced by the API as the sum of every
      // remaining occurrence window run through the rate engine individually —
      // never as one continuous stay. On the wire since the series-pricing api
      // change; typed once @parkingbase/ecommerce-api-dev republishes.
      const seriesPricing = lookupResponse as {
        seriesRateTotal?: number;
        seriesProcessingFee?: number;
        seriesWindowCount?: number;
      };
      const isSeriesQuote =
        seriesPricing.seriesRateTotal != null && seriesPricing.seriesRateTotal >= 0;

      // Keep the event's own rate — the panel prices events from it, not the
      // garage's generic hourly rate (legacy parity). For a series, the quote
      // total replaces the single-window rate so the whole pipeline (panel
      // totals, Pay page, payment value) prices the series.
      const eventRate = isSeriesQuote
        ? {
            amountToPay: seriesPricing.seriesRateTotal ?? 0,
            rate: seriesPricing.seriesRateTotal ?? 0,
            oversizeRate: 0,
            validUntil: undefined,
            processingFee: seriesPricing.seriesProcessingFee ?? 0,
            seriesWindowCount: seriesPricing.seriesWindowCount,
          }
        : eventDetails && rod
          ? {
              amountToPay: rod.amountToPay ?? rod.rate ?? 0,
              rate: rod.rate ?? 0,
              oversizeRate: rod.oversizeRate ?? 0,
              validUntil: rod.validUntil,
            }
          : undefined;

      // PB-2947 (Independent flow): for an independent-booking event with dates
      // picked, the subset (series) quote is authoritative. A later WHOLE-EVENT
      // lookup — event-window hydration, or a requote that raced an empty
      // selection — must NOT downgrade a good subset eventRate to the whole-event
      // rate: usePayment would then see no seriesWindowCount, read "quote not
      // ready", and drop the total to $0. Preserve the existing subset quote.
      const prevReserveState = useReserveStore.getState();
      const prevEventRate = prevReserveState.couponResponse?.eventRate as
        | { seriesWindowCount?: number }
        | undefined;
      const evd = eventDetails as { recurring?: boolean; independentBooking?: boolean } | undefined;
      const independentWithPicks =
        evd?.recurring === true &&
        evd?.independentBooking === true &&
        prevReserveState.selectedOccurrenceStarts.length > 0;
      const finalEventRate =
        independentWithPicks && !isSeriesQuote && prevEventRate?.seriesWindowCount != null
          ? prevReserveState.couponResponse?.eventRate
          : eventRate;

      // Keep the winning rate output wholesale: the deep-link (rateId) flow
      // prices the panel and payment from it, and its validUntil drives the
      // same-price exit window — the garage search never sees the rateId, so
      // the garage's hourlyRates/entryExitWindows can't know any of this.
      const rateOutput = rod
        ? {
            rateId: rod.rateId,
            rateName: rod.rateName,
            rateType: rod.rateType,
            rate: rod.rate ?? 0,
            amountToPay: rod.amountToPay ?? rod.rate ?? 0,
            oversizeRate: rod.oversizeRate ?? 0,
            validUntil: rod.validUntil,
            chargeToAccountAmount: rod.chargeToAccountAmount,
            rateCycle: rod.rateCycle as CouponRateOutput['rateCycle'],
            billedStartDateTime: rod.billedStartDateTime,
            billedEndDateTime: rod.billedEndDateTime,
          }
        : undefined;

      const coupon = lookupResponse.coupon;
      const response: CouponValidateResponse = {
        valid: true,
        discountAmount,
        specialRateId: coupon?.specialRateId || lookupResponse.specialRateId || '',
        specialRateName: coupon?.couponName || lookupResponse.specialRateName || '',
        eventDetails,
        eventRate: finalEventRate,
        rateOutput,
        rateWithoutDiscount: lookupResponse.rateOutputWithoutDiscount?.rate,
        specialRateApplied: rod?.rateType === 'SPECIALRATE',
      };

      set({
        couponCode: request.couponCode ?? null,
        couponResponse: response,
        isCouponValidating: false,
      });
    } catch (error) {
      logger.error('Failed to validate coupon', error, {
        feature: 'reserve-store',
        route: 'validateCoupon',
      });
      set({
        couponError: normalizeCouponErrorMessage(getApiErrorMessage(error, 'Invalid coupon code')),
        isCouponValidating: false,
      });
    }
  },

  removeCoupon: () => {
    // couponRequired/couponRequiredMessage intentionally survive: removing
    // the coupon on a gated event re-arms the gate (message + blocked Pay)
    // instead of silently unlocking the booking. couponRateId is cleared so a
    // user-removed deep-link rate doesn't silently re-apply on a date change.
    set({ couponCode: null, couponRateId: null, couponResponse: null, couponError: null });
  },

  // Editing the coupon input dismisses a stale "invalid code" error without touching
  // the applied coupon/response — removeCoupon would drop a valid applied code.
  clearCouponError: () => {
    if (get().couponError !== null) set({ couponError: null });
  },

  clearMonthlyCouponError: () => {
    if (get().monthlyCouponError !== null) set({ monthlyCouponError: null });
  },

  applyMonthlyCoupon: async (company, garageId, couponCode, startDate) => {
    set({ isMonthlyCouponApplying: true, monthlyCouponError: null });
    try {
      const data = await permitRateService.applyCouponPermitRate(
        company,
        garageId,
        '',
        couponCode,
        startDate
      );
      set({
        monthlyCouponCode: couponCode,
        monthlyCouponResponse: data,
        isMonthlyCouponApplying: false,
      });
      return data;
    } catch (error) {
      logger.error('Failed to apply monthly coupon', error, {
        feature: 'reserve-store',
        route: 'applyMonthlyCoupon',
      });
      set({
        monthlyCouponError: normalizeCouponErrorMessage(
          getApiErrorMessage(error, 'Invalid coupon code')
        ),
        isMonthlyCouponApplying: false,
      });
      return null;
    }
  },

  removeMonthlyCoupon: () => {
    set({
      monthlyCouponCode: null,
      monthlyCouponResponse: null,
      monthlyCouponError: null,
    });
  },

  validateMonthlyLinkRate: async (company, garageId, specialRateId, couponCode, startDate) => {
    set({ monthlyLinkRateId: specialRateId });
    try {
      // A signed-out visitor is only let through on a reCAPTCHA token (validateRequestEither).
      const recaptchaToken = await executeRecaptcha('GUEST_PAYMENT');
      const data = await permitRateService.applyCouponPermitRate(
        company,
        garageId,
        specialRateId,
        couponCode,
        startDate,
        recaptchaToken
      );
      set({ monthlyCouponResponse: data });
      return { granted: true };
    } catch (error) {
      logger.error('Monthly link rate rejected by the api', error, {
        feature: 'reserve-store',
        route: 'validateMonthlyLinkRate',
      });
      set({ monthlyCouponResponse: null });
      return {
        granted: false,
        errorMessage: getApiErrorMessage(error, 'This discount is not applicable'),
      };
    }
  },

  selectGarage: (garageId: string) => {
    // Picking a garage starts a new checkout, so drop any stale hold — the
    // payment screen seeds a fresh deadline when it mounts.
    set({ selectedGarageId: garageId, paymentDeadlineAt: null });
  },

  selectRate: (rate: MonthlyRateSelection | null) => {
    // Don't clear pendingDrivers here — they're permit-scoped, not rate-scoped;
    // clearing wiped a driver already entered on a Change-plan switch (PB-3328).
    set({
      selectedRate: rate,
      permitCalculation: null,
      permitCalculationError: null,
    });
  },

  setSelectedVehicleId: (vehicleId: string | null) => {
    set({ selectedVehicleId: vehicleId });
  },

  updateSearchDates: (startDateTime: string, endDateTime: string) => {
    set((state) => ({
      searchParams: state.searchParams
        ? { ...state.searchParams, startDateTime, endDateTime }
        : null,
    }));
  },

  updateSearchVehicleType: (vehicleType: string) => {
    set((state) => ({
      searchParams: state.searchParams ? { ...state.searchParams, vehicleType } : null,
    }));
  },

  calculatePermit: async (request: PermitCalculateRequest) => {
    // Overlapping quotes can resolve out of order; only the latest request may write the store
    const requestId = ++latestPermitCalcRequest;
    set({ isCalculatingPermit: true, permitCalculationError: null });
    try {
      const result = await searchService.calculatePermit(request);
      if (requestId === latestPermitCalcRequest) {
        set({ permitCalculation: result, isCalculatingPermit: false });
      }
      return result;
    } catch (error) {
      logger.error('Failed to calculate permit', error, {
        feature: 'reserve-store',
        route: 'calculatePermit',
      });
      if (requestId !== latestPermitCalcRequest) return null;
      set({
        permitCalculation: null,
        isCalculatingPermit: false,
        permitCalculationError: getApiErrorMessage(error, 'Failed to calculate permit rates'),
      });
      return null;
    }
  },

  fetchEventsForGarage: async (garageId: string, startDateTime: string, endDateTime: string) => {
    // Deliberately does NOT reset selectedEventId: the direct event-link flow
    // selects the event FIRST and then fetches its occurrences — clearing the
    // selection here blanked the series panel (PB-2947). The events-tab flow
    // clears the selection itself before refetching (useGarageDetail).
    set({ isLoadingEvents: true, eventOccurrences: [] });
    try {
      const recaptchaToken = await executeRecaptcha('event_occurrences');
      const events = await searchService.getEventOccurrences(
        garageId,
        startDateTime,
        endDateTime,
        recaptchaToken
      );
      set({ eventOccurrences: events, isLoadingEvents: false });
    } catch (error) {
      logger.error('Failed to fetch events for garage', error, {
        feature: 'reserve-store',
        route: 'fetchEventsForGarage',
      });
      set({ eventOccurrences: [], isLoadingEvents: false });
    }
  },

  selectEvent: (eventId: string | null) => {
    // The require-coupon gate is per-event — reset it whenever the selection
    // changes; the next event-scoped lookup re-arms it if needed. Callers
    // must therefore selectEvent BEFORE the lookup that arms the gate.
    // Picked occurrences are also per-event, so they reset with it.
    set({
      selectedEventId: eventId,
      couponRequired: false,
      couponRequiredMessage: null,
      selectedOccurrenceStarts: [],
    });
  },

  clearSearch: () => {
    set({
      garageResults: [],
      zoneResults: [],
      isSearching: false,
      canBookNewPermit: true,
      selectedGarageId: null,
      selectedRate: null,
      selectedVehicleId: null,
      couponCode: null,
      couponRateId: null,
      couponResponse: null,
      isCouponValidating: false,
      couponError: null,
      couponRequired: false,
      couponRequiredMessage: null,
      monthlyCouponCode: null,
      monthlyCouponResponse: null,
      isMonthlyCouponApplying: false,
      monthlyCouponError: null,
      monthlyLinkRateId: null,
      permitCalculation: null,
      isCalculatingPermit: false,
      permitCalculationError: null,
      searchParams: null,
      pendingDrivers: [],
      guestPassCount: 0,
      selectedFeeIds: [],
      eventOccurrences: [],
      isLoadingEvents: false,
      selectedEventId: null,
      eventDateHold: null,
      selectedOccurrenceStarts: [],
      paymentDeadlineAt: null,
    });
  },
}));
